import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { CampusCatalog } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { PcAvatarEditor } from './PcAvatarEditor.js';
import { CampusAvatarEditor, defaultCampus } from './CampusAvatarEditor.js';
import { DEFAULT_PC, type AvatarDraft } from './pcAppearance.js';

/** Catalogue « monde du campus » de l'instance ; null tant qu'il n'est pas chargé ou si le paquet manque. */
function useCampusCatalog(headers: Record<string, string>): CampusCatalog | null {
  const [catalog, setCatalog] = useState<CampusCatalog | null>(null);
  const headersRef = useRef(headers);
  headersRef.current = headers;
  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/avatars/campus/catalog`, { headers: headersRef.current })
      .then(async (res) => (res.ok ? await res.json() as CampusCatalog : null))
      .then((c) => { if (alive && c && c.persos.length > 0) setCatalog(c); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return catalog;
}

/**
 * Éditeur d'avatar : Pixel Citizen, ou « Campus » (avatars dessinés par
 * Charles, apparence v3) quand l'instance a le paquet. Le choix du style
 * n'apparaît que dans ce cas ; changer de style garde le brouillon de l'autre.
 */
export function AvatarEditor({ value, onChange, headers, aside, compact = false }: {
  value: AvatarDraft;
  onChange: (draft: AvatarDraft) => void;
  headers: Record<string, string>;
  aside?: ReactNode;
  compact?: boolean;
}) {
  const catalog = useCampusCatalog(headers);
  const last = useRef<{ pc?: AvatarDraft; campus?: AvatarDraft }>({});
  if (value.v === 2) last.current.pc = value; else last.current.campus = value;

  const switchTo = (v: 2 | 3) => {
    if (v === value.v) return;
    if (v === 2) onChange(last.current.pc ?? { v: 2, pc: DEFAULT_PC });
    else if (catalog) onChange(last.current.campus ?? { v: 3, campus: defaultCampus(catalog) });
  };

  const styleSwitch = (catalog || value.v === 3) && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span className="pl-field-label" style={{ margin: 0 }}>Style</span>
      <div role="radiogroup" aria-label="Style d'avatar" className="pl-choices">
        {([[2, 'Pixel Citizen'], [3, 'Campus']] as const).map(([v, label]) => (
          <button key={v} type="button" role="radio" aria-checked={value.v === v}
            className="pl-choice" onClick={() => switchTo(v)}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    // flex: 1 + minHeight: 0 : dans un parent de hauteur limitée, l'éditeur prend la place qui reste
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0 }}>
      {styleSwitch}
      {value.v === 3 && catalog
        ? <CampusAvatarEditor value={value.campus} catalog={catalog} onChange={(campus) => onChange({ v: 3, campus })}
            headers={headers} aside={aside} compact={compact} />
        : value.v === 3
          ? <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Chargement des avatars Campus…</p>
          : <PcAvatarEditor value={value.pc} onChange={(pc) => onChange({ v: 2, pc })} headers={headers} aside={aside} compact={compact} />}
    </div>
  );
}
